import { useEffect, useState, useMemo, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { RotateCcw, RefreshCw, Smartphone, ShieldCheck, Users, GraduationCap, History } from "lucide-react";

const DOT = { online: "bg-emerald-500", away: "bg-amber-400", offline: "bg-slate-300" };
const DOT_LABEL = { online: "Online", away: "Away", offline: "Offline" };
const CLASS_ORDER = ["Play Group", "LKG", "UKG", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10"];
const classLabel = (c) => (["LKG", "UKG", "Play Group"].includes(c) ? c : `Class ${c}`);

function StatusDot({ status }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-slate-500">
      <span className={`h-2.5 w-2.5 rounded-full ${DOT[status] || DOT.offline}`} />
      {DOT_LABEL[status] || "Offline"}
    </span>
  );
}

export default function AdminSessions() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [resettingId, setResettingId] = useState(null);
  const [tab, setTab] = useState("staff");
  const [classFilter, setClassFilter] = useState("all");
  const [sectionFilter, setSectionFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/sessions");
      setData(res.data);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not load sessions");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const resetDevice = async (student) => {
    setResettingId(student.id);
    try {
      await api.post(`/students/${student.id}/reset-device`);
      toast.success(`Device reset for ${student.name}`);
      await load();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Reset failed");
    } finally {
      setResettingId(null);
    }
  };

  const allStudents = data?.students || [];
  const staff = data?.staff || [];
  const counts = data?.counts || {};

  const availableClasses = useMemo(
    () => [...new Set(allStudents.map((s) => `${s.class_name}`))].sort(
      (a, b) => (CLASS_ORDER.indexOf(a) === -1 ? 99 : CLASS_ORDER.indexOf(a)) - (CLASS_ORDER.indexOf(b) === -1 ? 99 : CLASS_ORDER.indexOf(b))
    ),
    [allStudents]
  );
  const availableSections = useMemo(
    () => classFilter === "all" ? [] : [...new Set(allStudents.filter((s) => `${s.class_name}` === classFilter && s.section).map((s) => s.section))].sort(),
    [allStudents, classFilter]
  );

  // reset section when class changes
  useEffect(() => { setSectionFilter("all"); }, [classFilter]);

  const students = allStudents.filter((s) => {
    if (classFilter !== "all" && `${s.class_name}` !== classFilter) return false;
    if (sectionFilter !== "all" && s.section !== sectionFilter) return false;
    if (!q.trim()) return true;
    const t = q.trim().toLowerCase();
    return (s.name || "").toLowerCase().includes(t)
      || `${s.class_name}`.toLowerCase().includes(t)
      || (s.roll_number || "").toLowerCase().includes(t);
  });

  return (
    <div className="space-y-6" data-testid="admin-sessions-panel">
      {/* Summary */}
      <div className="flex flex-wrap items-center gap-3">
        <StatCard icon={Users} label="Staff online" value={counts.staff_online ?? "—"} tone="sky" />
        <StatCard icon={GraduationCap} label="Students online" value={counts.students_online ?? "—"} tone="emerald" />
        <StatCard icon={ShieldCheck} label="Devices registered" value={counts.students_bound ?? "—"} tone="violet" />
        <Button variant="outline" size="sm" onClick={load} disabled={loading} className="rounded-full ml-auto" data-testid="sessions-refresh-btn">
          <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="bg-slate-100 dark:bg-slate-800/60" data-testid="sessions-tabs">
          <TabsTrigger value="staff" data-testid="sessions-tab-staff" className="gap-1.5"><Users className="h-4 w-4" /> Staff</TabsTrigger>
          <TabsTrigger value="students" data-testid="sessions-tab-students" className="gap-1.5"><GraduationCap className="h-4 w-4" /> Students</TabsTrigger>
        </TabsList>

        {/* Staff */}
        <TabsContent value="staff" className="mt-5">
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-800">
            {staff.length === 0 && <div className="p-4 text-sm text-slate-500">No staff found.</div>}
            {staff.map((t) => (
              <div key={t.id} className="flex items-center justify-between gap-3 px-4 py-2.5" data-testid={`staff-session-${t.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">{t.name}</div>
                  <div className="text-xs text-slate-500 capitalize">{(t.role || "teacher").replace("_", " ")}</div>
                </div>
                <div className="flex items-center gap-4 shrink-0">
                  <span className="text-xs text-slate-400 hidden sm:block">
                    {t.last_login_at ? `Last login ${new Date(t.last_login_at).toLocaleString()}` : "Never signed in"}
                  </span>
                  <StatusDot status={t.status} />
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* Students */}
        <TabsContent value="students" className="mt-5">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <Select value={classFilter} onValueChange={setClassFilter}>
              <SelectTrigger className="h-9 w-36" data-testid="sessions-class-filter"><SelectValue placeholder="Class" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All classes</SelectItem>
                {availableClasses.map((c) => <SelectItem key={c} value={c} data-testid={`sessions-class-opt-${c}`}>{classLabel(c)}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={sectionFilter} onValueChange={setSectionFilter} disabled={classFilter === "all" || availableSections.length === 0}>
              <SelectTrigger className="h-9 w-32" data-testid="sessions-section-filter"><SelectValue placeholder="Section" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All sections</SelectItem>
                {availableSections.map((s) => <SelectItem key={s} value={s} data-testid={`sessions-section-opt-${s}`}>Section {s}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name / roll"
              className="h-9 max-w-[220px] ml-auto"
              data-testid="sessions-student-search"
            />
          </div>
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-800">
            {students.length === 0 && <div className="p-4 text-sm text-slate-500">No students match.</div>}
            {students.slice(0, 300).map((s) => (
              <div key={s.id} className="flex items-center justify-between gap-3 px-4 py-2.5" data-testid={`student-session-${s.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">{s.name}</div>
                  <div className="text-xs text-slate-500">Class {s.class_name}{s.section ? `-${s.section}` : ""} · Roll {s.roll_number || "—"}</div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className={`inline-flex items-center gap-1.5 text-xs ${s.device_bound ? "text-emerald-600" : "text-slate-400"}`} title={s.device_label || ""}>
                    <Smartphone className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">{s.device_bound ? (s.device_label || "Registered") : "No device"}</span>
                  </span>
                  <StatusDot status={s.status} />
                  {s.device_bound && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => resetDevice(s)}
                      disabled={resettingId === s.id}
                      className="rounded-full text-rose-600 border-rose-200 hover:bg-rose-50"
                      data-testid={`reset-device-${s.id}`}
                    >
                      <RotateCcw className="h-3.5 w-3.5 sm:mr-1.5" />
                      <span className="hidden sm:inline">{resettingId === s.id ? "…" : "Reset"}</span>
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
          {students.length > 300 && (
            <p className="text-xs text-slate-400 mt-2">Showing first 300 — refine your search to see more.</p>
          )}

          {/* Recent device resets (audit trail) */}
          <section className="mt-6">
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-3 flex items-center gap-2">
              <History className="h-4 w-4 text-slate-400" /> Recent device resets
            </h3>
            <div className="rounded-xl border border-slate-200 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-800">
              {(data?.recent_resets || []).length === 0 && (
                <div className="p-4 text-sm text-slate-500" data-testid="no-resets">No device resets yet.</div>
              )}
              {(data?.recent_resets || []).map((r, i) => (
                <div key={i} className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm" data-testid={`reset-audit-${i}`}>
                  <div className="min-w-0">
                    <span className="font-medium text-slate-800 dark:text-slate-100">{r.student_name || "Student"}</span>
                    <span className="text-slate-500"> (Class {r.class_name}{r.section ? `-${r.section}` : ""})</span>
                    <span className="text-slate-500"> reset by </span>
                    <span className="font-medium text-slate-700 dark:text-slate-200 capitalize">{r.actor_name || (r.actor_role || "staff").replace("_", " ")}</span>
                  </div>
                  <span className="text-xs text-slate-400 shrink-0">
                    {r.created_at ? new Date(r.created_at).toLocaleString() : ""}
                  </span>
                </div>
              ))}
            </div>
          </section>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, tone }) {
  const tones = {
    sky: "bg-sky-50 text-sky-600 dark:bg-sky-900/30 dark:text-sky-300",
    emerald: "bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-300",
    violet: "bg-violet-50 text-violet-600 dark:bg-violet-900/30 dark:text-violet-300",
  };
  return (
    <div className="flex items-center gap-3 rounded-xl border border-slate-200 dark:border-slate-700 px-4 py-2.5">
      <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${tones[tone]}`}>
        <Icon className="h-4 w-4" />
      </div>
      <div>
        <div className="text-lg font-semibold text-slate-900 dark:text-slate-100 leading-none">{value}</div>
        <div className="text-xs text-slate-500">{label}</div>
      </div>
    </div>
  );
}
